// The collaboration endpoints the studio uses (threads, inbox, review status, approvals, activity,
// members, presence ticket), on top of the shared `api.request`. Responses are the API's snake_case.
import type { Api } from "../api.ts";
import type { Notification, UnreadCount } from "./inbox.ts";
import type { Anchor, Message, Thread, ThreadDetail, ThreadList, ThreadTarget } from "./threads.ts";

export type ReviewState = "in_progress" | "needs_review" | "changes_requested" | "approved";

export interface ReviewStatus {
  id: string;
  target_type: "shot" | "episode";
  target_id: string;
  status: ReviewState;
  asset_version_id: string | null;
  note: string | null;
  set_by: string | null;
  set_at: string;
}

export interface Approval {
  id: string;
  target_type: string;
  target_id: string;
  asset_version_id: string | null;
  gate: string;
  decision: "approved" | "rejected" | "changes_requested";
  decided_by: string | null;
  decided_at: string;
  stale_since: string | null;
  note: string | null;
}

export interface ActivityItem {
  id: string;
  source: "generation" | "selection" | "review_comment" | "review_thread" | "approval" | "review_status";
  kind: string;
  at: string;
  actor_id: string | null;
  actor_name: string | null;
  ref_type: string;
  ref_id: string;
  data: Record<string, unknown>;
}

export interface Member {
  user_id: string;
  role: string;
  email: string | null;
  display_name: string | null;
}

export interface Posted {
  thread: Thread;
  message: Message;
  mentioned: string[];
  ambiguous: string[];
  replied: string[];
}

export interface MarkupUploaded {
  asset_version_id: string;
  asset_id: string;
  sha256: string;
  size: number;
  mime: "image/png";
  width: number;
  height: number;
}

export type ActivityTarget =
  | "shot"
  | "take"
  | "asset_version"
  | "episode"
  | "scene"
  | "element"
  | "canvas"
  | "canvas_node"
  | "folder";

export function reviewApi(api: Api, ws: string) {
  const w = `/workspaces/${ws}`;
  return {
    threads: (q: {
      targetType: ThreadTarget;
      targetId: string;
      assetVersionId?: string | null;
      status?: "open" | "resolved" | "all";
    }) =>
      api.request<ThreadList>("GET", `${w}/threads`, {
        query: {
          targetType: q.targetType,
          targetId: q.targetId,
          assetVersionId: q.assetVersionId ?? undefined,
          status: q.status ?? "all",
        },
      }),
    thread: (id: string) => api.request<ThreadDetail>("GET", `${w}/threads/${id}`),
    createThread: (b: {
      targetType: ThreadTarget;
      targetId: string;
      assetVersionId?: string | null;
      anchor?: Anchor | null;
      /** a drawing note: the annotated frame uploaded with uploadMarkup */
      markupAssetVersionId?: string | null;
      body: string;
    }) =>
      api.request<Posted>("POST", `${w}/threads`, {
        body: {
          targetType: b.targetType,
          targetId: b.targetId,
          ...(b.assetVersionId ? { assetVersionId: b.assetVersionId } : {}),
          ...(b.anchor ? { anchor: b.anchor } : {}),
          ...(b.markupAssetVersionId ? { markupAssetVersionId: b.markupAssetVersionId } : {}),
          body: b.body,
        },
      }),
    /** The annotated frame of a drawing note (the frame plus the drawing, a PNG data URL) on a take or version. */
    uploadMarkup: (b: { targetType: "take" | "asset_version"; targetId: string; png: string }) =>
      api.request<MarkupUploaded>("POST", `${w}/threads/markup`, { body: b }),
    reply: (id: string, body: string) =>
      api.request<Posted>("POST", `${w}/threads/${id}/messages`, { body: { body } }),
    resolve: (id: string) => api.request<Thread>("POST", `${w}/threads/${id}/resolve`, { body: {} }),
    reopen: (id: string) => api.request<Thread>("POST", `${w}/threads/${id}/reopen`, { body: {} }),
    carry: (id: string, assetVersionId: string) =>
      api.request<Posted & { created?: boolean }>("POST", `${w}/threads/${id}/carry`, {
        body: { assetVersionId },
      }),

    inbox: (q: { unread?: boolean; limit?: number } = {}) =>
      api.request<{ items: Notification[]; next_cursor: string | null }>("GET", `${w}/inbox`, {
        query: { unread: q.unread === undefined ? undefined : String(q.unread), limit: q.limit ?? 30 },
      }),
    unreadCount: () => api.request<UnreadCount>("GET", `${w}/inbox/unread-count`),
    markRead: (b: { ids?: string[]; all?: boolean; unread?: boolean }) =>
      api.request<{ changed: number }>("POST", `${w}/inbox/read`, { body: b }),

    reviewStatus: (target: "shot" | "episode", id: string) =>
      api.request<{ current: ReviewStatus | null; history: ReviewStatus[] }>(
        "GET",
        `${w}/${target}s/${id}/review-status`,
      ),
    setReviewStatus: (
      target: "shot" | "episode",
      id: string,
      b: { status: ReviewState; note?: string; assetVersionId?: string | null },
    ) =>
      api.request<{ status: ReviewStatus; approval: Approval | null; notified: string[] }>(
        "POST",
        `${w}/${target}s/${id}/review-status`,
        {
          body: {
            status: b.status,
            ...(b.note ? { note: b.note } : {}),
            ...(b.assetVersionId ? { assetVersionId: b.assetVersionId } : {}),
          },
        },
      ),
    approvals: (targetType: "shot" | "episode", targetId: string) =>
      api.request<{ items: Approval[]; current: Approval[] }>("GET", `${w}/approvals`, {
        query: { targetType, targetId },
      }),
    activity: (targetType: ActivityTarget, targetId: string) =>
      api.request<{ items: ActivityItem[] }>("GET", `${w}/activity`, {
        query: { targetType, targetId, limit: 50 },
      }),

    members: () => api.request<{ items: Member[] }>("GET", `${w}/members`, { query: { limit: 200 } }),
    me: () => api.request<{ user?: { id?: string; email?: string; display_name?: string } }>("GET", "/me"),
    /** Ticket for this person's inbox socket (see inboxClient.ts). */
    inboxSession: () =>
      api.request<{ ticket: string; path: string; expiresInS: number }>("POST", `${w}/inbox/session`, {
        body: {},
        asking: true,
      }),
    presenceSession: (projectId: string) =>
      api.request<{ ticket: string; path: string; expiresInS: number }>(
        "POST",
        `${w}/projects/${projectId}/presence-session`,
        { body: {} },
      ),
  };
}

export type ReviewApi = ReturnType<typeof reviewApi>;

export const REVIEW_LABEL: Record<ReviewState, string> = {
  in_progress: "In progress",
  needs_review: "Waiting for sign-off",
  changes_requested: "Changes requested",
  approved: "Signed off",
};

/** The display name of a member (or a fallback built from the email). */
export const memberName = (m: Pick<Member, "display_name" | "email"> | undefined): string =>
  m?.display_name || m?.email?.split("@")[0] || "Someone";

/** What to type after @ to mention a member: the email local part (unique, and what the API matches first). */
export const memberHandle = (m: Pick<Member, "email" | "display_name">): string =>
  (m.email?.split("@")[0] ?? m.display_name ?? "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}._-]+/gu, "-")
    .replace(/^[-._]+|[-._]+$/g, "");
